import React, { useCallback, useEffect, useRef, useState } from 'react'

import format from 'date-fns/format'
import getDay from 'date-fns/getDay'
import enUS from 'date-fns/locale/en-US'
import parse from 'date-fns/parse'
import startOfWeek from 'date-fns/startOfWeek'
import {
  Calendar,
  DateRange,
  type EventProps,
  dateFnsLocalizer,
} from 'react-big-calendar'

import 'react-big-calendar/lib/css/react-big-calendar.css'

import {
  AgendaViewEventWrapper,
  EventWrapperProps,
  MonthViewEventWrapper,
} from '@/components/common/EventCalendar/EventWrapper'
import { Header } from '@/components/common/EventCalendar/Header'
import { Toolbar } from '@/components/common/EventCalendar/Toolbar'
import type { Event } from '@/components/common/EventCalendar/types'

const locales = {
  'en-US': enUS,
}

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
})

interface EventCalendar {
  events: Event[]
  onEventClick: (event: Event) => void
}

const EventCalendar: React.FC<EventCalendar> = ({ events, onEventClick }) => {
  const parentRef = useRef<HTMLDivElement>(null)

  const [height, setHeight] = useState(0)

  const components = {
    eventWrapper: (props: EventWrapperProps) => (
      <MonthViewEventWrapper {...props} onEventClick={onEventClick} />
    ),
    header: Header,
    toolbar: Toolbar,
    agenda: {
      event: (props: EventWrapperProps) => (
        <AgendaViewEventWrapper {...props} onEventClick={onEventClick} />
      ),
    },
  }

  const formatAgendaDateRange = useCallback(
    (range: DateRange) => {
      return `${format(range.start, 'LLL do, yyyy')} - ${format(range.end, 'LLL do, yyyy')}`
    },
    [format],
  )

  useEffect(() => {
    const el = parentRef.current
    if (!el) return

    const observer = new ResizeObserver(() => {
      setHeight(el.offsetHeight)
    })
    observer.observe(el)

    return () => observer.disconnect()
  }, [])

  return (
    <div ref={parentRef} className="h-full">
      <Calendar
        localizer={localizer}
        events={events}
        startAccessor="start"
        endAccessor="end"
        style={{ height }}
        // @ts-expect-error
        components={components}
        drilldownView="agenda"
        messages={{
          noEventsInRange: 'No events in range.',
        }}
        formats={{
          agendaHeaderFormat: formatAgendaDateRange,
        }}
      />
    </div>
  )
}

export { Event }

export default EventCalendar
